/**
 * Structural sharing after a clone-and-mutate command: every part of `next` that is deep-equal to the same
 * place in `prev` is replaced by prev's object, so unchanged slices keep their identity. The 4 Hz clock
 * tick then leaves rooms, inventory, wallet… untouched by reference, and selectors / views can skip work
 * with a plain `===` (M4-13). `next` is updated in place; plain objects and arrays only (the save is JSON).
 */
export function structuralShare<T>(prev: T, next: T): T {
  if (Object.is(prev, next)) return prev;
  if (!isShareable(prev) || !isShareable(next) || Array.isArray(prev) !== Array.isArray(next)) return next;
  const p = prev as Record<string, unknown>;
  const n = next as Record<string, unknown>;
  const keys = Object.keys(n);
  let same = keys.length === Object.keys(p).length;
  for (const key of keys) {
    const shared = structuralShare(p[key], n[key]);
    if (shared !== n[key]) n[key] = shared;
    if (shared !== p[key] || !Object.hasOwn(p, key)) same = false;
  }
  return same ? prev : next;
}

function isShareable(v: unknown): v is object {
  if (v === null || typeof v !== 'object') return false;
  if (Array.isArray(v)) return true;
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}
